import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import AirOutlinedIcon from "@mui/icons-material/AirOutlined";
import OpacityOutlinedIcon from "@mui/icons-material/OpacityOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import UmbrellaOutlinedIcon from "@mui/icons-material/UmbrellaOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { getBranchDetails, getWeatherConditions } from "../../../../../config/Services";
import "./WeatherWidget.css";

const toFixedTemp = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  if (parsed > 100) return Math.round(parsed - 273.15);
  return Math.round(parsed);
};

const getDateLabel = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
};

const DEFAULT_DAILY = [
  { label: "Tomorrow", icon: "sun", temp: null },
  { label: "Wed", icon: "cloud", temp: null },
  { label: "Thu", icon: "sun", temp: null },
];

const EMPTY_WEATHER = {
  temperature: null,
  feelsLike: null,
  condition: null,
  humidity: null,
  windKmh: null,
  uvIndex: null,
  precipitation: null,
  visibility: null,
  dayLabel: "Today",
  dateLabel: getDateLabel(),
  iconBaseUri: null,
  daily: DEFAULT_DAILY,
};

const iconByType = {
  sun: <WbSunnyOutlinedIcon sx={{ fontSize: "1.1rem" }} />,
  rain: <UmbrellaOutlinedIcon sx={{ fontSize: "1.1rem" }} />,
  cloud: <CloudOutlinedIcon sx={{ fontSize: "1.1rem" }} />,
};

const getConditionIconType = (conditionText = "") => {
  if (/rain|drizzle|storm|shower/i.test(conditionText)) return "rain";
  if (/cloud|mist|haze|fog|overcast/i.test(conditionText)) return "cloud";
  return "sun";
};

const normalizeWeather = (apiResponse) => {
  if (!apiResponse) return EMPTY_WEATHER;
  const root = apiResponse?.data || apiResponse;

  // Google Weather API (currentConditions:lookup) response shape
  if (root?.weatherCondition !== undefined || root?.temperature?.degrees !== undefined) {
    const windValue = root?.wind?.speed?.value ?? null;
    const windKmh =
      windValue != null
        ? root?.wind?.speed?.unit === "KILOMETERS_PER_HOUR"
          ? Math.round(windValue)
          : Math.round(windValue * 3.6)
        : null;
    return {
      temperature: root?.temperature?.degrees != null ? Math.round(root.temperature.degrees) : null,
      feelsLike:
        root?.feelsLikeTemperature?.degrees != null
          ? Math.round(root.feelsLikeTemperature.degrees)
          : null,
      condition: root?.weatherCondition?.description?.text || null,
      humidity: root?.relativeHumidity != null ? Math.round(root.relativeHumidity) : null,
      windKmh,
      uvIndex: root?.uvIndex ?? null,
      precipitation:
        root?.precipitation?.probability?.percent != null
          ? Math.round(root.precipitation.probability.percent)
          : null,
      visibility: root?.visibility?.distance ?? null,
      dayLabel: "Today",
      dateLabel: getDateLabel(root?.currentTime),
      iconBaseUri: root?.weatherCondition?.iconBaseUri || null,
      daily: DEFAULT_DAILY,
    };
  }

  // Generic / other API shapes
  const current = root?.current || root?.currentWeather || root?.weather || root;
  const weatherNode = current?.weather?.[0] || {};
  const conditionObj = current?.condition || {};
  const forecastRows =
    root?.daily || root?.forecast?.daily || root?.forecast || root?.nextDays || [];

  const normalized = {
    temperature: toFixedTemp(
      current?.temp_c ??
        current?.temperatureC ??
        current?.temperature ??
        current?.temp ??
        current?.main?.temp
    ),
    feelsLike: null,
    condition: conditionObj?.text || weatherNode?.main || weatherNode?.description || null,
    humidity: current?.humidity != null ? Math.round(Number(current.humidity)) : null,
    windKmh: current?.wind_kph != null ? Math.round(Number(current.wind_kph)) : null,
    uvIndex: current?.uv ?? current?.uvi ?? current?.uvIndex ?? null,
    precipitation: null,
    dayLabel: "Today",
    dateLabel: getDateLabel(current?.last_updated || current?.date || root?.date),
    iconBaseUri: null,
    daily: [],
  };

  normalized.daily = (Array.isArray(forecastRows) ? forecastRows : [])
    .slice(0, 3)
    .map((row, idx) => ({
      label:
        row?.label ||
        row?.day ||
        row?.weekday ||
        (row?.date
          ? new Date(row.date).toLocaleDateString("en-US", { weekday: "short" })
          : idx === 0
          ? "Tomorrow"
          : "Day"),
      icon: getConditionIconType(
        row?.condition?.text || row?.summary || row?.weather?.[0]?.main || ""
      ),
      temp: toFixedTemp(row?.temp ?? row?.avgtemp_c ?? row?.temperature ?? row?.main?.temp),
    }));

  return normalized;
};

const WEATHER_TTL_MS = 5 * 60 * 1000; // 5 minutes

const normalizeForecastDays = (forecastData) => {
  const days = forecastData?.forecastDays || [];
  return days.slice(1, 4).map((day, idx) => {
    const conditionText =
      day?.daytimeForecast?.weatherCondition?.description?.text ||
      day?.nighttimeForecast?.weatherCondition?.description?.text || "";
    const label =
      idx === 0
        ? "Tomorrow"
        : day?.interval?.startTime
        ? new Date(day.interval.startTime).toLocaleDateString("en-US", { weekday: "short" })
        : `Day ${idx + 2}`;
    return {
      label,
      icon: getConditionIconType(conditionText),
      temp: day?.maxTemperature?.degrees != null ? Math.round(day.maxTemperature.degrees) : null,
    };
  });
};

// Module-level cache — survives component remounts (PosNavbar re-renders across pages)
const weatherCache = {
  data: null,
  fetchedAt: 0,
  intervalId: null,
  subscribers: new Set(),
  isLoading: false,
};

const notifySubscribers = (weather, loading) => {
  weatherCache.subscribers.forEach((fn) => fn(weather, loading));
};

const doFetch = async (lat, lon) => {
  if (weatherCache.isLoading) return;
  weatherCache.isLoading = true;
  notifySubscribers(weatherCache.data, true);
  try {
    const res = await getWeatherConditions(lat, lon, 4);
    const weatherData = res?.data?.data?.weatherData;
    const weatherForecast = res?.data?.data?.weatherForecast;

    const weather = normalizeWeather(weatherData);
    weather.daily = normalizeForecastDays(weatherForecast);
    weatherCache.data = weather;
    weatherCache.fetchedAt = Date.now();
    notifySubscribers(weatherCache.data, false);
  } catch (e) {
    notifySubscribers(weatherCache.data, false);
  } finally {
    weatherCache.isLoading = false;
  }
};

const startWeatherPolling = (lat, lon) => {
  // Already polling with an active interval — nothing to do
  if (weatherCache.intervalId) return;

  const isFresh = weatherCache.data && Date.now() - weatherCache.fetchedAt < WEATHER_TTL_MS;
  if (!isFresh) doFetch(lat, lon);

  weatherCache.intervalId = setInterval(() => doFetch(lat, lon), WEATHER_TTL_MS);
};

function WeatherWidget({
  latitude: latProp,
  longitude: longProp,
  city,
  country,
  onChangeLocation,
}) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [weather, setWeather] = useState(weatherCache.data || EMPTY_WEATHER);

  const locationLabel = useMemo(() => {
    if (!city) return "Bangalore, IN";
    return `${city}, ${country?.slice(0, 2)?.toUpperCase() || "IN"}`;
  }, [city, country]);

  useEffect(() => {
    const subscriber = (newWeather, loading) => {
      if (newWeather) setWeather(newWeather);
      setIsLoading(loading);
    };
    weatherCache.subscribers.add(subscriber);

    // If cache has fresh data, use it immediately without fetching
    const isFresh = weatherCache.data && Date.now() - weatherCache.fetchedAt < WEATHER_TTL_MS;
    if (isFresh) {
      setWeather(weatherCache.data);
    }

    const resolveCoords = () => {
      if (latProp && longProp) {
        startWeatherPolling(latProp, longProp);
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => startWeatherPolling(pos.coords.latitude, pos.coords.longitude),
          () => {
            const locId = localStorage.getItem("locId");
            if (!locId) return;
            getBranchDetails(locId)
              .then((res) => {
                const addr = res?.data?.branch?.addresses?.[0];
                if (addr?.latitude && addr?.longitude) {
                  startWeatherPolling(addr.latitude, addr.longitude);
                }
              })
              .catch(() => {});
          }
        );
      } else {
        const locId = localStorage.getItem("locId");
        if (!locId) return;
        getBranchDetails(locId)
          .then((res) => {
            const addr = res?.data?.branch?.addresses?.[0];
            if (addr?.latitude && addr?.longitude) {
              startWeatherPolling(addr.latitude, addr.longitude);
            }
          })
          .catch(() => {});
      }
    };

    if (!isFresh) resolveCoords();

    return () => {
      weatherCache.subscribers.delete(subscriber);
    };
  }, [latProp, longProp]);

  const resolveAndFetch = () => {
    const fetch = (lat, lon) => {
      weatherCache.fetchedAt = 0; // invalidate cache so doFetch always fires
      doFetch(lat, lon);
    };
    if (latProp && longProp) {
      fetch(latProp, longProp);
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => fetch(pos.coords.latitude, pos.coords.longitude),
        () => {
          const locId = localStorage.getItem("locId");
          if (!locId) return;
          getBranchDetails(locId)
            .then((res) => {
              const addr = res?.data?.branch?.addresses?.[0];
              if (addr?.latitude && addr?.longitude) fetch(addr.latitude, addr.longitude);
            })
            .catch(() => {});
        }
      );
    } else {
      const locId = localStorage.getItem("locId");
      if (!locId) return;
      getBranchDetails(locId)
        .then((res) => {
          const addr = res?.data?.branch?.addresses?.[0];
          if (addr?.latitude && addr?.longitude) fetch(addr.latitude, addr.longitude);
        })
        .catch(() => {});
    }
  };

  const handleOpen = () => {
    resolveAndFetch();
    setOpen(true);
  };

  const weatherIcon = weather.iconBaseUri ? (
    <img
      src={`${weather.iconBaseUri}.png`}
      alt={weather.condition}
      className="weather-widget-icon-img"
    />
  ) : (
    <WbSunnyOutlinedIcon sx={{ fontSize: "1.1rem", color: "inherit" }} />
  );

  const dash = <span className="weather-widget-dash">--</span>;

  return (
    <>
      <button className="weather-widget-chip" type="button" onClick={handleOpen}>
        <div className="weather-widget-icon-container">
          {weatherIcon}
        </div>
        <div className="weather-widget-chip-text">
          <div className="weather-widget-chip-text-top">
            <span className="weather-widget-chip-temp">
              {weather.temperature != null ? (
                <>
                  {weather.temperature}
                  {"\u00B0"}C
                </>
              ) : (
                dash
              )}
            </span>
            <span className="weather-widget-chip-condition">
              {weather.condition ? weather.condition.toUpperCase() : dash}
            </span>
          </div>
          <span className="weather-widget-chip-location">{locationLabel}</span>
        </div>
        <KeyboardArrowDownIcon sx={{ fontSize: "1rem", color: "#64748b", flexShrink: 0, marginLeft: "8px" }} />
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="weather-widget-title"
        aria-describedby="weather-widget-body"
        className="weather-widget-modal-root"
      >
        <Box className="weather-widget-modal">
          {/* ── Header: temp + condition / day + date ── */}
          <div className="weather-widget-top">
            <div className="weather-widget-top-left">
              <div className="weather-widget-main-temp">
                {weather.temperature != null ? <>{weather.temperature}{"\u00B0"}</> : dash}
              </div>
              <div className="weather-widget-condition-text">
                {weather.condition ?? dash}
              </div>
            </div>
            <div className="weather-widget-day-info">
              <div className="weather-widget-today">{weather.dayLabel.toUpperCase()}</div>
              <div className="weather-widget-date">{weather.dateLabel}</div>
            </div>
            <div className="weather-widget-bg-icon" aria-hidden="true" />
          </div>

          {/* ── 3 metric cards ── */}
          <div className="weather-widget-metrics">
            <div className="weather-widget-metric-card">
              <AirOutlinedIcon sx={{ fontSize: "1.1rem", color: "#93c5fd" }} />
              <span>{weather.windKmh != null ? `${weather.windKmh} km/h` : dash}</span>
              <small>Wind</small>
            </div>
            <div className="weather-widget-metric-card">
              <OpacityOutlinedIcon sx={{ fontSize: "1.1rem", color: "#93c5fd" }} />
              <span>{weather.humidity != null ? `${weather.humidity}%` : dash}</span>
              <small>Humidity</small>
            </div>
            <div className="weather-widget-metric-card">
              <LightModeOutlinedIcon sx={{ fontSize: "1.1rem", color: "#fcd34d" }} />
              <span>{weather.uvIndex != null ? `${weather.uvIndex} UV` : dash}</span>
              <small>Index</small>
            </div>
          </div>


          {/* ── Forecast rows ── */}
          {weather.daily.length > 0 && (
            <div className="weather-widget-forecast-list" id="weather-widget-body">
              {weather.daily.map((day, index) => (
                <div className="weather-widget-forecast-row" key={`${day.label}-${index}`}>
                  <span className="weather-widget-forecast-day">{day.label}</span>
                  <span className="weather-widget-forecast-icon">{iconByType[day.icon]}</span>
                  <span className="weather-widget-forecast-temp">
                    {day.temp != null ? <>{day.temp}{"\u00B0"}</> : dash}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* ── Footer: location + change button ── */}
          <div className="weather-widget-footer">
            <button
              className="weather-widget-location-btn"
              type="button"
              onClick={onChangeLocation}
            >
              Change location
            </button>
            <span>{isLoading ? "Syncing..." : locationLabel}</span>
          </div>
        </Box>
      </Modal>
    </>
  );
}

WeatherWidget.propTypes = {
  latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  city: PropTypes.string,
  country: PropTypes.string,
  onChangeLocation: PropTypes.func,
};

WeatherWidget.defaultProps = {
  latitude: "",
  longitude: "",
  city: "",
  country: "",
  onChangeLocation: () => {},
};

export default WeatherWidget;
